import React from 'react';
import { Laptop, Users, Smartphone, Monitor, Tv, Printer, Layers } from 'lucide-react';

/**
 * Компонент для выбора устройства и контекста использования
 */
const DeviceContextSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  deviceTypes, 
  targetAudiences 
}) => {
  // Получение иконки для типа устройства
  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'desktop': return <Monitor className="w-5 h-5" />;
      case 'laptop': return <Laptop className="w-5 h-5" />;
      case 'tablet': return <Layers className="w-5 h-5" />;
      case 'mobile': return <Smartphone className="w-5 h-5" />;
      case 'tv': return <Tv className="w-5 h-5" />;
      case 'print': return <Printer className="w-5 h-5" />;
      default: return <Laptop className="w-5 h-5" />;
    }
  };

  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Laptop className="w-5 h-5 mr-2 text-blue-500" />
        Устройство и контекст
      </h3>
      
      <div className="space-y-4">
        {/* Тип устройства */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Тип устройства
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {deviceTypes.map((device) => (
              <label 
                key={device.id}
                className={`
                  flex items-center p-2 rounded-lg cursor-pointer
                  ${formData.deviceType === device.id 
                    ? 'bg-blue-100 dark:bg-blue-800 border-blue-200 dark:border-blue-700' 
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                  }
                  border hover:bg-blue-50 dark:hover:bg-blue-900/50 transition-colors
                `}
              >
                <input
                  type="radio"
                  name="deviceType"
                  value={device.id}
                  checked={formData.deviceType === device.id}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <span className="mr-2 text-blue-500 dark:text-blue-400">
                  {getDeviceIcon(device.id)}
                </span>
                <div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {device.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {device.defaultFontSize}px
                  </div>
                </div>
              </label>
            ))}
          </div>
          {errors.deviceType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.deviceType}
            </p>
          )}
        </div>

        {/* Целевая аудитория */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 flex items-center">
            <Users className="w-4 h-4 mr-2 text-blue-500" />
            Целевая аудитория
          </label>
          <select
            id="targetAudience"
            name="targetAudience"
            value={formData.targetAudience}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {targetAudiences.map((audience) => (
              <option key={audience.id} value={audience.id}>
                {audience.name}
              </option>
            ))}
          </select>
          {errors.targetAudience && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.targetAudience}
            </p>
          )}
        </div>

        {/* Информация о размерах для устройства и аудитории */}
        <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="text-blue-500 dark:text-blue-400 mt-0.5">
              {getDeviceIcon(formData.deviceType)}
            </div>
            <div>
              <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">
                {deviceTypes.find(d => d.id === formData.deviceType)?.name}
              </h4>
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-1">
                Базовый размер шрифта: <strong>{deviceTypes.find(d => d.id === formData.deviceType)?.defaultFontSize}px</strong>
                {formData.deviceType === 'mobile' && ' (мобильные устройства требуют более крупных шрифтов для удобочитаемости)'}
                {formData.deviceType === 'tv' && ' (увеличенные размеры из-за большего расстояния просмотра)'}
                {formData.deviceType === 'print' && ' (печатные материалы обычно используют меньшие размеры шрифта)'}
              </p>
              <p className="text-xs text-blue-600 dark:text-blue-400">
                {formData.targetAudience === 'general' && 'Рекомендация для общей аудитории: 16-18px для основного текста.'}
                {formData.targetAudience === 'elderly' && 'Рекомендация для пожилых людей: увеличенный шрифт (минимум 18px) и четкий контраст.'}
                {formData.targetAudience === 'children' && 'Рекомендация для детей: крупный шрифт (18-20px) и простые гарнитуры без засечек.'}
                {formData.targetAudience === 'visually-impaired' && 'Рекомендация для людей с нарушением зрения: крупный шрифт (минимум 20px) и высокий контраст.'}
                {formData.targetAudience === 'dyslexic' && 'Рекомендация для людей с дислексией: увеличенные межстрочные интервалы и простые шрифты без засечек.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceContextSection;